/**
 * Дисциплины и отрезок матча — одним словарём на весь экран.
 *
 * Имена едут с сервера (`sportName`, `/v1/meta.sportNames`). Этот файл —
 * запас на случай, когда сервер код знает, а подпись ещё не доехала, и
 * единственный источник слова отрезка: «карта» на теннисе читалась как
 * поломка, а не как сет.
 *
 * Приставка `other:` адресована движку («в матчинге не участвует») и человеку
 * не показывается. Знакомый хвост переводится; незнакомый остаётся их словом.
 */

/** Теннис, настольный, волейбол, бадминтон: в ключе P1 — сет, не карта. */
const SET_SPORTS = new Set(['tennis', 'table-tennis', 'volleyball', 'badminton']);

/**
 * Как дисциплина называется вслух. Регистр один: так же, как теннис и футбол
 * уже стоят в сите. CS2 оставляем как говорят. Остальное — по-русски, без
 * латиницы вперемешку с «table tennis».
 */
const SPORTS: Record<string, string> = {
  cs2: 'CS2',
  cs: 'CS2',
  'cs-go': 'CS2',
  csgo: 'CS2',
  'counter-strike': 'CS2',
  dota2: 'дота 2',
  dota: 'дота 2',
  'dota-2': 'дота 2',
  lol: 'лига легенд',
  'league-of-legends': 'лига легенд',
  leagueoflegends: 'лига легенд',
  valorant: 'валорант',
  vrant: 'валорант',
  rsix: 'Rainbow Six',
  r6: 'Rainbow Six',
  'rainbow-six': 'Rainbow Six',
  sc2: 'старкрафт 2',
  starcraft: 'старкрафт 2',
  'starcraft-2': 'старкрафт 2',
  darts: 'дартс',
  dart: 'дартс',
  mlbb: 'мобайл легендс',
  'mobile-legends': 'мобайл легендс',
  mma: 'мма',
  ufc: 'мма',
  tennis: 'теннис',
  soccer: 'футбол',
  football: 'футбол',
  futbol: 'футбол',
  basketball: 'баскетбол',
  hockey: 'хоккей',
  'ice-hockey': 'хоккей',
  baseball: 'бейсбол',
  'american-football': 'американский футбол',
  americanfootball: 'американский футбол',
  'table-tennis': 'настольный теннис',
  tabletennis: 'настольный теннис',
  ttennis: 'настольный теннис',
  'ping-pong': 'настольный теннис',
  volleyball: 'волейбол',
  volley: 'волейбол',
  boxing: 'бокс',
  box: 'бокс',
  handball: 'гандбол',
  cricket: 'крикет',
  esports: 'киберспорт',
  esport: 'киберспорт',
  other: 'другое',
};

/** Семейство отрезка в ключе рынка: матч / карта / сет / четверть / тайм / иннинг. */
export const SPAN_NAMES: Record<string, string> = {
  M: 'матч',
  P: 'карта',
  S: 'сет',
  Q: 'четверть',
  H: 'тайм',
  I: 'иннинг',
};

/** Фишка сита: «без карт», не «без карта». */
export const SPAN_CHIP: Record<string, string> = {
  M: 'матча',
  P: 'карт',
  S: 'сетов',
  Q: 'четвертей',
  H: 'таймов',
  I: 'иннингов',
};

/**
 * Сырой код к одному ключу. Два зеркала пишут `CS:GO` и `cs2`, `Dota 2` и
 * `dota2` — без сводки в сите стоят две фишки с одним именем.
 */
const CANON: Record<string, string> = {
  cs: 'cs2',
  'cs-2': 'cs2',
  'cs-go': 'cs2',
  csgo: 'cs2',
  'counter-strike': 'cs2',
  'counterstrike': 'cs2',
  dota: 'dota2',
  'dota-2': 'dota2',
  дота: 'dota2',
  дота2: 'dota2',
  'дота-2': 'dota2',
  'league-of-legends': 'lol',
  leagueoflegends: 'lol',
  'лига-легенд': 'lol',
  лигалегенд: 'lol',
  vrant: 'valorant',
  валорант: 'valorant',
  football: 'soccer',
  futbol: 'soccer',
  футбол: 'soccer',
  'ice-hockey': 'hockey',
  icehockey: 'hockey',
  хоккей: 'hockey',
  tabletennis: 'table-tennis',
  ttennis: 'table-tennis',
  'ping-pong': 'table-tennis',
  'настольный-теннис': 'table-tennis',
  volley: 'volleyball',
  волейбол: 'volleyball',
  ufc: 'mma',
  мма: 'mma',
  box: 'boxing',
  бокс: 'boxing',
  r6: 'rsix',
  'rainbow-six': 'rsix',
  'радуга-6': 'rsix',
  радуга6: 'rsix',
  starcraft: 'sc2',
  'starcraft-2': 'sc2',
  'старкрафт-2': 'sc2',
  старкрафт2: 'sc2',
  старкрафт: 'sc2',
  dart: 'darts',
  дартс: 'darts',
  'mobile-legends': 'mlbb',
  'мобайл-легендс': 'mlbb',
  americanfootball: 'american-football',
  amfootball: 'american-football',
  'am-football': 'american-football',
  nfl: 'american-football',
  'американский-футбол': 'american-football',
  баскетбол: 'basketball',
  бейсбол: 'baseball',
  теннис: 'tennis',
  гандбол: 'handball',
  крикет: 'cricket',
  киберспорт: 'esports',
  esport: 'esports',
};

/** Код дисциплины к сравнимому виду: `other:table_tennis` и `Table Tennis` — одно. */
export function sportKey(raw: string): string {
  let key = raw.trim().toLowerCase();
  if (key.startsWith('other:')) key = key.slice('other:'.length);
  return key.replace(/[:._\s]+/g, '-');
}

/** Один код на все написания: `CS:GO`, `csgo`, `other:cs2` → `cs2`. */
export function sportCanon(raw: string | undefined): string {
  if (!raw) return '';
  const key = sportKey(raw);
  return CANON[key] ?? key;
}

const BASKETS = new Set(['', 'esports', 'other']);

/**
 * Фишки сита: один код на дисциплину. `dota2` и «дота 2», `CS2` и `cs2`
 * иначе стоят двумя кнопками. Корзины `esports` / `other` не дисциплины.
 */
export function foldSports(codes: Iterable<string>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of codes) {
    if (typeof raw !== 'string' || raw === '') continue;
    const code = sportCanon(raw);
    if (BASKETS.has(code) || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

/**
 * Коды фишек дисциплин в сите: свой список, увиденное вкладкой и выбранное.
 * Голый номер («1», «109») — чужой внутренний id спорта, не дисциплина: из
 * списка и памяти вкладки он не показывается. Выбранный раньше остаётся,
 * чтобы его можно было снять.
 */
export function sieveSportCodes(
  base: Iterable<string>,
  seen: Iterable<string>,
  picked: Iterable<string>,
): string[] {
  const named = foldSports([...base, ...seen]).filter((code) => !/^\d+$/.test(code));
  return foldSports([...named, ...picked]);
}

/** Одна кнопка на видимое имя: два кода с подписью «CS2» не стоят рядом. */
export function uniqSportChips<T extends { name: string; count: number }>(chips: readonly T[]): T[] {
  const out: T[] = [];
  const at = new Map<string, number>();
  for (const one of chips) {
    const key = one.name.trim().toLowerCase();
    const i = at.get(key);
    if (i === undefined) {
      at.set(key, out.length);
      out.push({ ...one });
      continue;
    }
    const prev = out[i]!;
    out[i] = { ...prev, count: prev.count + one.count };
  }
  return out;
}

/**
 * Фишки дисциплин в сите — закрытый пул, не текущий кадр.
 * Иначе баскетбол нельзя выключить, пока в ленте его нет (Антон 20.09).
 * `esports` / `other` — не дисциплины, а корзины.
 */
export const SIEVE_SPORTS: readonly string[] = [
  ...new Set(Object.keys(SPORTS).map((code) => sportCanon(code))),
].filter((code) => code !== '' && code !== 'esports' && code !== 'other');

/** Подпись строки: слово сервера главнее, код — запас. */
export function sportTitle(fork: { sport?: string; sportName?: string }): string {
  // Код с зеркала — `rainbow-six`, подпись кадра ещё «радуга 6». Имя на экране
  // берём из словаря, иначе лента и сито живут со старым словом до выката v2.
  if (sportCanon(fork.sport) === 'rsix' || sportCanon(fork.sportName) === 'rsix') {
    return sportLabel('rsix');
  }
  const given = typeof fork.sportName === 'string' ? fork.sportName.trim() : '';
  if (given !== '' && given !== '—') return given;
  const label = sportLabel(fork.sport);
  // Корзина `other` без имени — не дисциплина. В шапке карточки это шум
  // («(другое)» рядом с матчем), в сите слово берётся из каталога по коду.
  if (label === 'другое' || label === '—') return '—';
  return label;
}

const LEAGUE_DASH = /^[—–−?-]+$/;

/** Лига словом. Прочерк Вилки не показываем — это не турнир. */
export function leagueTitle(fork: { sport?: string; sportName?: string; league?: string }): string {
  const sport = sportTitle(fork);
  const league = typeof fork.league === 'string' ? fork.league.trim() : '';
  if (league === '' || LEAGUE_DASH.test(league)) return sport;
  if (sport === '—' || sport === league) return league;
  return `${sport} · ${league}`;
}

export function sportLabel(code: string | undefined): string {
  if (!code) return '—';
  const canon = sportCanon(code);
  const known = SPORTS[canon] ?? SPORTS[sportKey(code)] ?? SPORTS[code.trim().toLowerCase()];
  if (known !== undefined) return known;
  const raw = code.trim();
  if (raw.toLowerCase().startsWith('other:')) {
    return raw.slice('other:'.length).replace(/[_-]+/g, ' ');
  }
  return raw;
}

export function isSetSport(sport: string | undefined): boolean {
  return sport !== undefined && SET_SPORTS.has(sportKey(sport));
}

/** Период из ключа `market:scope:period:side:line:subject`. Нет ключа — null. */
export function marketPeriod(key: string | undefined): string | null {
  if (!key) return null;
  const parts = key.split(':');
  if (parts.length !== 6) return null;
  const period = parts[2] ?? '';
  return period === '' ? null : period;
}

/** Первая буква периода: M / P / S / Q / H / I. Незнакомое — null, не догадка. */
export function periodFamily(period: string | null | undefined): string | null {
  if (!period) return null;
  const head = period[0];
  if (head === undefined || !'MPSQHI'.includes(head)) return null;
  return head;
}

/**
 * Ставка не на матч целиком, а на карту, сет, четверть, тайм, иннинг.
 *
 * Какой отрезок идёт СЕЙЧАС, площадки нам не говорят. Поэтому «идущий» в
 * фильтре — это все такие ставки в лайве, не одна «текущая».
 */
export function isStretchPeriod(period: string | null | undefined): boolean {
  return period !== null && period !== undefined && period !== 'M' && period !== 'U';
}

/**
 * Четвёртая карта: период `P4` в ключе, не линия «4» и не «карт 24.5».
 *
 * В теннисе тот же `P4` — сет, его сюда не кладём: фишка сита зовётся
 * «исход 4 карты», и сет ей врать не должен.
 */
export function isMap4(key: string | undefined, sport?: string): boolean {
  return isMapN(4, key, sport);
}

/** Исход третьей карты — тот же разбор, что у четвёртой. */
export function isMap3(key: string | undefined, sport?: string): boolean {
  return isMapN(3, key, sport);
}

const ORDINAL: Record<number, string> = { 3: '3rd|third', 4: '4th|fourth' };
const MAP_SAID: Record<number, readonly RegExp[]> = Object.fromEntries([3, 4].map((n) => [n, [
  new RegExp(`(?:^|[^\\d.])(?:карта|карт[аыу]?|map)\\s*${n}(?![\\d.])`, 'i'),
  new RegExp(`(?:^|[^\\d.])${n}(?:-?я|-?й)?\\s+(?:карта|карт[аыу]?|map)(?![\\d.])`, 'i'),
  new RegExp(`\\b(?:${ORDINAL[n]})\\s+map\\b`, 'i'),
]]));

function isMapN(n: 3 | 4, key: string | undefined, sport?: string): boolean {
  if (isSetSport(sport)) return false;
  const period = marketPeriod(key);
  if (period !== null) return period === `P${n}` || period.startsWith(`P${n}H`);
  const m = (key ?? '').toLowerCase();
  if (m === '') return false;
  return MAP_SAID[n]!.some((re) => re.test(m));
}

export function periodSaid(period: string, sport?: string): string {
  if (period === '' || period === 'M' || period === 'U') return '';
  const named: Record<string, string> = {
    S1: '1-й сет',
    S2: '2-й сет',
    S3: '3-й сет',
    S4: '4-й сет',
    S5: '5-й сет',
    H1: '1-й тайм',
    H2: '2-й тайм',
    Q1: '1-я четверть',
    Q2: '2-я четверть',
    Q3: '3-я четверть',
    Q4: '4-я четверть',
  };
  if (named[period] !== undefined) return named[period]!;
  const half = period.match(/^P([1-9])H([12])$/);
  if (half !== null) {
    const unit = isSetSport(sport) ? 'сет' : 'карта';
    const nth = isSetSport(sport) ? 'й' : 'я';
    return `${half[1]}-${nth} ${unit}, ${half[2]}-й тайм`;
  }
  const map = period.match(/^P([1-9])/);
  if (map !== null) return isSetSport(sport) ? `${map[1]}-й сет` : `${map[1]}-я карта`;
  const inn = period.match(/^I([1-9])$/);
  if (inn !== null) return `${inn[1]}-й иннинг`;
  return period;
}

export function scopeSaid(scope: string, sport?: string): string {
  if (scope === 'PE') return isSetSport(sport) ? 'по сетам' : 'по картам';
  if (scope === 'PO') return 'по очкам';
  if (scope === 'SP') return 'по спреду';
  return '';
}

/** Группы дисциплин в сите: сначала кибер, потом командные, единоборства, ракетки. */
export const SPORT_GROUPS: readonly { id: string; name: string; codes: readonly string[] }[] = [
  { id: 'cyber', name: 'киберспорт', codes: ['cs2', 'dota2', 'lol', 'valorant', 'rsix', 'sc2', 'mlbb', 'overwatch', 'call-of-duty', 'rocket-league', 'king-of-glory'] },
  { id: 'team', name: 'командные', codes: ['soccer', 'basketball', 'hockey', 'volleyball', 'handball', 'baseball', 'american-football', 'futsal', 'rugby', 'cricket'] },
  { id: 'fight', name: 'единоборства', codes: ['mma', 'boxing'] },
  { id: 'racket', name: 'ракетки', codes: ['tennis', 'table-tennis', 'badminton', 'squash'] },
];

export function sportGroup(code: string): string {
  const canon = sportCanon(code);
  return SPORT_GROUPS.find((group) => group.codes.includes(canon))?.id ?? 'other';
}

/**
 * Значок дисциплины для сита (`public/sports/`). Кибер — логотипы игр с их
 * сайтов, спорт — Twemoji (CC-BY 4.0, © Twitter / jdecked).
 */
const SPORT_ICON: Record<string, string> = {
  cs2: 'cs2.png',
  dota2: 'dota2.png',
  lol: 'lol.png',
  valorant: 'valorant.png',
  rsix: 'rsix.png',
  sc2: 'sc2.png',
  mlbb: 'mlbb.jpg',
  soccer: 'soccer.svg',
  futsal: 'soccer.svg',
  basketball: 'basketball.svg',
  hockey: 'hockey.svg',
  volleyball: 'volleyball.svg',
  handball: 'handball.svg',
  baseball: 'baseball.svg',
  'american-football': 'american-football.svg',
  boxing: 'boxing.svg',
  mma: 'mma.svg',
  tennis: 'tennis.svg',
  'table-tennis': 'table-tennis.svg',
  darts: 'darts.svg',
  badminton: 'badminton.svg',
  squash: 'badminton.svg',
  rugby: 'rugby.svg',
  cricket: 'cricket.svg',
};

export function sportIconOf(code: string): string | null {
  const file = SPORT_ICON[sportCanon(code)];
  return file === undefined ? null : `${import.meta.env.BASE_URL}sports/${file}`;
}
